"use client";

import { useRaceLeaderboard } from "@/lib/api/leaderboards";
import { useIdentity } from "@/lib/api/session";
import {
  RACE_VALUE_LABEL,
  boardError,
  money,
  ordinal,
  raceMetricValue,
  type RaceEntry,
  type RaceKind,
  type RaceToken,
} from "@/lib/leaderboards/format";
import { Board, type BoardRow } from "./Board";

/* The standings of one race, kept current by the browser. The server hands
   over the first frame; from then on the board refetches every few seconds,
   the way the whitelabel's RaceLeaderboardTable does, so a settling bet
   moves a player up while the page is open.

   Signed in, the player is told where they stand in a line above the board —
   or, if nowhere yet, whatever the race's own unranked text says. */

export function RaceBoard({
  raceId,
  kind,
  token,
  initial,
  unrankedText,
  emptyBody,
}: {
  raceId: string;
  kind: RaceKind;
  token: RaceToken | null;
  initial?: RaceEntry[];
  unrankedText?: string;
  emptyBody: string;
}) {
  const board = useRaceLeaderboard(raceId, initial);
  const { identity } = useIdentity();

  const rows: BoardRow[] = (board.data?.data ?? []).map((entry) => ({
    id: entry.account.id,
    nickname: entry.account.isHidden ? "Hidden" : entry.account.nickname,
    isHidden: entry.account.isHidden,
    value: raceMetricValue(entry, token),
    prize: money(entry.prize, token),
  }));

  const mine = identity ? rows.findIndex((row) => row.id === identity.id) : -1;
  const note = !identity
    ? undefined
    : mine >= 0
      ? `You are ${ordinal(mine + 1)} with ${rows[mine].value}, in line for ${rows[mine].prize}.`
      : unrankedText || "You are not on the board yet. A qualifying bet puts you on it.";

  return (
    <Board
      title="Race standings"
      live
      note={note}
      rows={rows}
      valueLabel={RACE_VALUE_LABEL[kind]}
      loading={board.isPending}
      error={board.isError ? boardError(board.error) : null}
      emptyTitle="No qualifying bets yet"
      emptyBody={emptyBody}
      selfId={identity?.id}
    />
  );
}
